// src/components/pages/Login.jsx
import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { Link, useNavigate } from "react-router-dom";

import AppShell from "../layout/AppShell";
import Logo from "../layout/Logo";
import Button from "../ui/Button";
import Field, { PasswordField } from "../ui/Field";
import Card from "../ui/Card";

import axiosInstance from "../../utils/AxiosInstance";
import {
  clearPendingVerificationEmail,
  getPendingVerificationEmail,
  setPasswordResetEmail,
  setPendingVerificationEmail,
} from "../../utils/authStorage";
import { loginUser } from "../../redux/actions/userActions";

/**
 * Email + password login. Mirrors the existing API contract exactly:
 *   POST /security/auth/login   { email, password, stayLoggedIn }
 * Auth is set via HttpOnly cookie by the server.
 */
export default function Login() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [stay, setStay] = useState(false);
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [pendingEmail] = useState(() => getPendingVerificationEmail());

  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const onEmailChange = (e) => {
    setEmail(e.target.value);
    setFieldErrors((fe) => (fe.email ? { ...fe, email: undefined } : fe));
  };

  const onPasswordChange = (e) => {
    setPassword(e.target.value);
    setFieldErrors((fe) => (fe.password ? { ...fe, password: undefined } : fe));
  };

  const validate = () => {
    const errs = {};
    if (!email.trim()) errs.email = "Please enter your email.";
    else if (!EMAIL_RE.test(email.trim())) errs.email = "Enter a valid email address.";
    if (!password) errs.password = "Please enter your password.";
    return errs;
  };

  const isUnverifiedAccountMessage = (m) => {
    const t = String(m || "").toLowerCase();
    return (
      t.includes("not yet been activated") ||
      t.includes("not activated") ||
      t.includes("inactive") ||
      t.includes("activate your account")
    );
  };

  const buildUser = (data) => ({
    id: data?.id,
    name: data?.name,
    email: data?.email,
    type: data?.type,
    account: data?.account,
    credits: data?.account?.credit,
    buyDrawActiveMegaMillions: data?.buyDrawActiveMegaMillions,
    buyDrawActivePowerBall: data?.buyDrawActivePowerBall,
  });

  const notifyLoggedIn = (user) => {
    window.dispatchEvent(
      new CustomEvent("auth:changed", {
        detail: { authenticated: true, source: "login-page", user },
      })
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    const errs = validate();
    setFieldErrors(errs);
    if (Object.keys(errs).length > 0) return;

    setSubmitting(true);

    try {
      const res = await axiosInstance.post("/security/auth/login", {
        email,
        password,
        stayLoggedIn: stay,
      });

      const user = buildUser(res.data);
      if (!user.id) throw new Error("Login succeeded but no user payload returned.");

      clearPendingVerificationEmail();
      dispatch(loginUser(user));
      notifyLoggedIn(user);
      navigate("/dashboard", { state: { credits: user.credits, name: user.name } });
    } catch (err) {
      const status = err?.response?.status;
      const backendMessage =
        typeof err?.response?.data === "string"
          ? err.response.data
          : err?.response?.data?.message || err?.response?.data?.error;
      const normalizedEmail = String(email || "").trim().toLowerCase();

      if (normalizedEmail && isUnverifiedAccountMessage(backendMessage)) {
        setPendingVerificationEmail(normalizedEmail);
        navigate("/verification", {
          state: { email: normalizedEmail, operation: "verification" },
        });
        return;
      }

      if (status === 401 || status === 403) {
        setError("Invalid email or password.");
      } else {
        setError(backendMessage || err?.message || "Login failed. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleForgot = () => {
    const e = String(email || "").trim();
    if (!e) {
      setError("Enter your email first, then click Forgot password.");
      return;
    }
    setPasswordResetEmail(e);
    navigate("/verification", { state: { email: e, operation: "password-reset" } });
  };

  return (
    <AppShell bare>
      <div className="min-h-screen flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-[420px]">

          <div className="flex justify-center mb-6">
            <Logo size="lg" onClick={() => navigate("/")} />
          </div>

          <Card>
            <h1
              className="font-display font-semibold mb-1.5"
              style={{ fontSize: 24, color: "var(--txt)" }}
            >
              Welcome back.
            </h1>
            <p className="text-[13px] mb-5" style={{ color: "var(--txt-2)" }}>
              Log in to view your dashboard and generate new picks.
            </p>

            {error && (
              <div
                className="mb-4 p-3 rounded-[10px] text-[13px] flex items-start gap-2"
                style={{
                  background: "var(--red-dim)",
                  border: "1px solid var(--red-border)",
                  color: "var(--red)",
                }}
              >
                <i className="ti ti-alert-circle" style={{ fontSize: 16, marginTop: 1 }} />
                <span>{error}</span>
              </div>
            )}

            <form onSubmit={handleSubmit} noValidate>
              <Field
                label="Email address"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={onEmailChange}
                autoComplete="email"
                error={fieldErrors.email}
              />
              <PasswordField
                value={password}
                onChange={onPasswordChange}
                autoComplete="current-password"
                error={fieldErrors.password}
              />

              <label
                className="flex items-center gap-2 text-[12px] mb-5 cursor-pointer"
                style={{ color: "var(--txt-2)" }}
              >
                <input
                  type="checkbox"
                  checked={stay}
                  onChange={(e) => setStay(e.target.checked)}
                  style={{ accentColor: "var(--gold-lt)" }}
                />
                Stay logged in
              </label>

              <Button type="submit" full loading={submitting}>
                {submitting ? "Logging in..." : "Log in"}
              </Button>
            </form>

            <button
              type="button"
              onClick={handleForgot}
              className="mt-4 text-[12px] hover:underline"
              style={{ color: "var(--txt-2)" }}
            >
              Forgot your password?
            </button>

            {pendingEmail && (
              <div
                className="mt-5 p-3 rounded-[10px] text-[12px]"
                style={{
                  background: "var(--gold-dim)",
                  border: "1px solid var(--gold-border)",
                  color: "var(--gold-lt)",
                }}
              >
                <p className="font-medium mb-1">Finish verifying your account</p>
                <p className="mb-3 break-all" style={{ color: "var(--txt-2)" }}>
                  Pending verification for {pendingEmail}.
                </p>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() =>
                    navigate("/verification", {
                      state: { email: pendingEmail, operation: "verification" },
                    })
                  }
                >
                  Continue verification
                </Button>
              </div>
            )}
          </Card>

          <p
            className="mt-5 text-center text-[13px]"
            style={{ color: "var(--txt-2)" }}
          >
            New here?{" "}
            <Link
              to="/sign-up"
              className="font-medium hover:underline"
              style={{ color: "var(--gold-lt)" }}
            >
              Create an account
            </Link>
          </p>
        </div>
      </div>
    </AppShell>
  );
}
